import { type Format } from '@ugin/shared';
import { Swords } from 'lucide-react';
import { Link } from 'react-router-dom';

import { type WidgetComponent, type WidgetMeta } from '../_base/types';
import { WidgetContainer } from '../_base/WidgetContainer';

import { type MatchSummary } from '@/api/types';
import { useMatches } from '@/hooks/useArena';
import { confidenceText, resultTone } from '@/lib/stats';
import { cn, formatRelativeTime } from '@/lib/utils';


const meta: WidgetMeta = {
  id: 'recent-matches',
  name: 'Recent matches',
  description: 'What Arena has handed over, newest first.',
  icon: Swords,
  defaultSize: { w: 7, h: 6 },
  category: 'performance',
};

/** Why a match has no archetype label, in words Rowan can act on. */
export const UNKNOWN_REASON_TEXT: Readonly<Record<string, string>> = {
  no_centroids: 'No archetype data for this format yet — the nightly refresh builds it.',
  no_cards: 'They never revealed a card, so there was nothing to go on.',
  too_few_matches: 'Too few distinctive cards showed up to name a deck.',
  ambiguous: 'The evidence split between several archetypes, so Ugin will not guess.',
};

export interface RecentMatchesWidgetProps {
  format?: Format;
  deckId?: string;
  limit?: number;
}

export const RecentMatchesWidget: WidgetComponent<RecentMatchesWidgetProps> = ({
  format,
  deckId,
  limit = 10,
}) => {
  const { data, isLoading, error, refetch } = useMatches({
    ...(format && { format }),
    ...(deckId && { deckId }),
    limit,
  });
  const matches = data?.matches ?? [];

  return (
    <WidgetContainer
      meta={meta}
      icon={<Swords className="size-4" />}
      loading={isLoading}
      error={error ?? undefined}
      onRetry={() => void refetch()}
      empty={!isLoading && matches.length === 0}
      emptyTitle="No matches yet"
      emptyDescription="Once Detailed Logs is on and you play a game, matches land here within a minute."
    >
      {matches.length > 0 ? (
        <ul className="space-y-1.5">
          {matches.map((match) => (
            <li key={match.id}>
              <MatchRow match={match} />
            </li>
          ))}
        </ul>
      ) : null}
    </WidgetContainer>
  );
};

RecentMatchesWidget.meta = meta;

const TONE_CLASS: Record<'win' | 'loss' | 'draw', string> = {
  win: 'bg-ugin-success/15 text-ugin-success',
  loss: 'bg-ugin-danger/15 text-ugin-danger',
  draw: 'bg-ugin-warn/15 text-ugin-warn',
};

function MatchRow({ match }: { match: MatchSummary }) {
  const tone = resultTone(match.result);
  const game = match.game;
  const details = [
    game?.onPlay === true ? 'on the play' : game?.onPlay === false ? 'on the draw' : null,
    game === null ? null : `${String(game.turns)} turns`,
    game && game.myMulligans > 0 ? `${String(game.myMulligans)} mull` : null,
  ].filter((d): d is string => d !== null);

  return (
    <Link
      to={`/matches/${match.id}`}
      className="flex items-center gap-3 rounded-lg border border-ugin-border/40 bg-ugin-surface/40 px-3 py-2.5 transition-all hover:border-ugin-border hover:bg-ugin-surface-elevated"
    >
      <span
        className={cn(
          'grid size-7 shrink-0 place-items-center rounded-md font-mono text-xs font-semibold uppercase',
          TONE_CLASS[tone],
        )}
        aria-label={tone}
      >
        {tone === 'win' ? 'W' : tone === 'loss' ? 'L' : 'D'}
      </span>

      <span className="min-w-0 flex-1">
        <span className="flex flex-wrap items-baseline gap-x-2 text-sm">
          <span className="text-ugin-text">{match.opponentName ?? 'Unknown opponent'}</span>
          <Archetype match={match} />
        </span>
        <span className="mt-0.5 flex flex-wrap items-baseline gap-x-2 text-xs text-ugin-text-faint">
          <span>{match.deckName ?? 'Unlinked deck'}</span>
          {details.map((d) => (
            <span key={d}>· {d}</span>
          ))}
        </span>
      </span>

      <span className="shrink-0 text-xs text-ugin-text-faint">
        {formatRelativeTime(match.playedAt)}
      </span>
    </Link>
  );
}

function Archetype({ match }: { match: MatchSummary }) {
  if (match.opponentArchetype !== null) {
    return (
      <span className="text-xs text-ugin-accent">
        {match.opponentArchetype}
        {match.opponentArchetypeConfidence === null ? null : (
          <span className="text-ugin-text-faint"> {confidenceText(match.opponentArchetypeConfidence)}</span>
        )}
      </span>
    );
  }
  const reason =
    match.classification?.kind === 'unknown'
      ? (UNKNOWN_REASON_TEXT[match.classification.reason] ?? 'Ugin could not name this deck.')
      : 'Not classified yet.';
  // An honest blank, with the reason one hover away — never a guessed label.
  return (
    <span className="text-xs italic text-ugin-text-faint" title={reason}>
      unknown
    </span>
  );
}
